/**
 * @file BlendMaterials.js
 * @version 2.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-08-17
 * @description The Materials tab: the raw material list, its prices and its
 *              analysis, as at a date.
 *
 *              This replaces the workbook's hidden Data sheet, which was
 *              IMPORTRANGE'd from a Google Sheet nobody could audit.
 *
 *              The grid is READ-ONLY. It was a spreadsheet — every cell a bare
 *              input, drafts in two maps, a Save button — and that made prices
 *              feel like a scratchpad rather than managed figures that every
 *              blend quote depends on. Editing now happens in BlendBookModal,
 *              which carries its own effective date and shows the update history
 *              beside the numbers. The old design also overloaded one date
 *              control as both the read as-of and the write effective date, so
 *              changing it silently discarded unsaved edits.
 *
 *              Price columns are shown as ROLES — SPOT / CURRENT / UPCOMING —
 *              resolved from today's date server-side, with the period name
 *              underneath. The underlying data stays stored per named period, so
 *              a rollover moves the roles and leaves the old prices intact.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import DateInput from './DateInput';
import Input from './Input';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import BlendMaterialModal from './BlendMaterialModal';
import BlendBookModal from './BlendBookModal';
import BlendMaterialHistoryModal from './BlendMaterialHistoryModal';
import BlendPriceImportModal from './BlendPriceImportModal';
import { useSlideOutButton } from './SlideOutButton';
import './SearchLayout.css';
import './TableStyles.css';
import './BlendCalculator.css';

const today = () => new Date().toISOString().slice(0, 10);

const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Costs are NUMERIC(12,4), so Postgres hands back "236.0000". Four decimals on a
 * whole-pound price is noise and overflows the cell. Trim to the shortest exact
 * representation — the value is unchanged, only its rendering.
 */
const trim = (v) => {
  const n = numOrNull(v);
  return n === null ? '' : String(n);
};

/** The header a price column shows. Role first; the stored label is the fallback. */
const ROLE_LABEL = { spot: 'SPOT', current: 'CURRENT', upcoming: 'UPCOMING' };

export default function BlendMaterials({ user }) {
  const [asOf, setAsOf] = usePersistedState('blendCosts.effectiveDate', today());
  const [search, setSearch] = usePersistedState('blendMaterials.search', '');
  const [showInactive, setShowInactive] = usePersistedState('blendMaterials.showInactive', false);

  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [editMaterial, setEditMaterial] = useState(null);
  const [bookModal, setBookModal] = useState(null);      // 'costs' | 'nutrients'
  const [historyFor, setHistoryFor] = useState(null);
  const [importing, setImporting] = useState(false);
  // Hidden rather than shown-and-failing when the server has no API key.
  const [importAvailable, setImportAvailable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/blends/book?asOf=${asOf}&includeInactive=${showInactive}`,
        { credentials: 'include' });
      if (res.status === 403) throw new Error('You do not have permission to view material costs');
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load materials');
      setBook(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [asOf, showInactive]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_URL}/api/blends/price-list/status`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((d) => setImportAvailable(Boolean(d.available)))
      .catch(() => {});
  }, []);

  // Resolved values as at the date shown, so a cell shows exactly what the
  // calculator would use for that date.
  const resolved = useMemo(() => {
    const costs = new Map();
    const nutrients = new Map();
    for (const row of book?.costs || []) {
      costs.set(`${row.material_id}:${row.price_column_id}`, row.cost_per_tonne);
    }
    for (const row of book?.nutrients || []) nutrients.set(row.material_id, row.values || {});
    return { costs, nutrients };
  }, [book]);

  const columns = useMemo(() => book?.columns || [], [book]);
  const nutrientComponents = useMemo(() => book?.nutrient_components || [], [book]);

  const rows = useMemo(() => {
    const list = book?.materials || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((m) =>
      [m.name, m.key, m.supplier_code, m.category, ...(m.aliases || [])]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [book, search]);

  const buildRowActions = useCallback((material) => [
    {
      key: 'edit',
      icon: 'edit',
      label: 'Edit material',
      onClick: () => setEditMaterial(material)
    },
    {
      key: 'history',
      icon: 'show_chart',
      label: 'Price history',
      onClick: () => setHistoryFor(material)
    }
  ], []);

  // Prices and analysis are managed for every material at once, so they are NOT
  // row actions — they sit in the generic list, reachable from the header
  // buttons and from a right-click anywhere in the table.
  const ctxMenu = useTableContextMenu({ rows, getRowId: (m) => m.id });

  useSlideOutButton([loading, bookModal, importAvailable]);

  return (
    <div className="search-layout blend-calculator blend-materials" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Materials</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>
              The raw material list, its prices and its analysis, as at the date below.
              Use Edit prices or Edit analysis to change them — each records the update
              under one entry you can review and revert.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => setBookModal('costs')}
                      disabled={loading || !columns.length}
                      title="Edit raw material prices">
                <div className="icon-wrapper"><span className="material-symbols-rounded">payments</span></div>
                <span className="btn-label">Edit prices</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => setBookModal('nutrients')}
                      disabled={loading || !nutrientComponents.length}
                      title="Edit raw material analysis">
                <div className="icon-wrapper"><span className="material-symbols-rounded">science</span></div>
                <span className="btn-label">Edit analysis</span>
              </button>
            </div>
            {importAvailable && (
              <div>
                <button className="btn-slideout" onClick={() => setImporting(true)}
                        disabled={loading || !columns.length}
                        title="Import prices from a supplier price list PDF">
                  <div className="icon-wrapper"><span className="material-symbols-rounded">upload_file</span></div>
                  <span className="btn-label">Import prices</span>
                </button>
              </div>
            )}
            <div>
              <button className="btn-slideout" onClick={() => setEditMaterial({})}
                      title="Add a raw material">
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">Add material</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={load} title="Reload">
                <div className="icon-wrapper"><span className="material-symbols-rounded">refresh</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 105px auto' }}>
          <div className="filter-group">
            <label>Search</label>
            <Input type="text" icon="search" value={search}
                   placeholder="Material, code or alias…"
                   onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Prices as at</label>
            <DateInput value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Show inactive</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="blend-materials-inactive" type="checkbox" checked={showInactive}
                       onChange={(e) => setShowInactive(e.target.checked)} />
                <label htmlFor="blend-materials-inactive"></label>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="search-content">
        {error && (
          <div className="blend-warning inline">
            <span className="material-symbols-rounded">error</span>
            <span>{error}</span>
            <button className="btn-icon" onClick={() => setError(null)}>
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        )}
        {message && !error && (
          <div className="blend-notice inline">
            <span className="material-symbols-rounded">check_circle</span>
            <span>{message}</span>
            <button className="btn-icon" onClick={() => setMessage(null)}>
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        )}

        {loading && (
          <div className="loading">
            <span className="material-symbols-rounded spinning">progress_activity</span>
            <span>Loading materials…</span>
          </div>
        )}

        {!loading && book && (
          <div className="blend-grid-wrap">
            <div className="table-container">
              <table className="matrix-table blend-cost-grid"
                     onContextMenu={ctxMenu.onTableContextMenu}>
                <thead>
                  <tr>
                    <th className="sticky-col-1">Material</th>
                    <th className="sticky-col-2 sticky-col-last actions-col">&nbsp;</th>
                    {columns.map((c, i) => (
                      <th key={c.key} className={`bg-num${i === 0 ? ' first-scrollable' : ''}`}>
                        {ROLE_LABEL[c.role] || c.label}
                        {/* The period this column's prices belong to. Spot has
                            none — spot is today's price, not a period. */}
                        {c.role !== 'spot' && c.period_name && (
                          <span className="blend-col-period">{c.period_name}</span>
                        )}
                      </th>
                    ))}
                    {nutrientComponents.map((c, i) => (
                      <th key={c.key}
                          className={`bg-num nutrient-col${i === 0 ? ' nutrient-col-first' : ''}`}
                          title={c.label}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((material) => (
                    <tr key={material.id}
                        onContextMenu={ctxMenu.onRowContextMenu(material)}
                        className={material.is_active ? '' : 'not-in-blend'}>
                      <td className="sticky-col-1">
                        <span className="blend-material-name">{material.name}</span>
                        <span className="blend-material-meta">
                          {material.category || 'uncategorised'}
                          {material.supplier_code ? ` · ${material.supplier_code}` : ''}
                          {material.components?.length
                            ? ` · → ${material.components.map((c) => c.label || c.key).join(', ')}`
                            : ' · not bridged'}
                        </span>
                      </td>
                      <td className="sticky-col-2 sticky-col-last actions-col"
                          onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu actions={buildRowActions(material)}
                                        triggerTitle="Material actions" />
                      </td>

                      {columns.map((column, i) => {
                        const value = resolved.costs.get(`${material.id}:${column.id}`);
                        // Spot and the current period are the two figures anyone
                        // actually quotes from, so they carry the weight.
                        // Upcoming is reference until its period starts.
                        const strong = column.role === 'spot' || column.role === 'current';
                        // null is "asked, not quoted" — a real answer, and
                        // distinct from never having been recorded.
                        return (
                          <td key={column.key}
                              className={`num${i === 0 ? ' first-scrollable' : ''}${strong ? ' price-strong' : ''}`}>
                            {value === null
                              ? <span className="muted">not quoted</span>
                              : trim(value) || <span className="muted">—</span>}
                          </td>
                        );
                      })}

                      {nutrientComponents.map((component, i) => (
                        <td key={component.key}
                            className={`num nutrient-col${i === 0 ? ' nutrient-col-first' : ''}`}>
                          {trim((resolved.nutrients.get(material.id) || {})[component.key])
                            || <span className="muted">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={2 + columns.length + nutrientComponents.length} className="muted">
                        No materials match that search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'prices', icon: 'payments', label: 'Edit prices', onClick: () => setBookModal('costs') },
          ...(importAvailable ? [{
            key: 'import', icon: 'upload_file', label: 'Import prices from a PDF',
            onClick: () => setImporting(true)
          }] : []),
          { key: 'analysis', icon: 'science', label: 'Edit analysis', onClick: () => setBookModal('nutrients') },
          { key: 'add', icon: 'add', label: 'Add material', onClick: () => setEditMaterial({}) },
          { key: 'reload', icon: 'refresh', label: 'Reload from server', onClick: load }
        ]}
      />

      {bookModal && (
        <BlendBookModal
          kind={bookModal}
          book={book}
          onClose={() => setBookModal(null)}
          onSaved={(msg) => { setMessage(msg); setError(null); load(); }}
        />
      )}

      {importing && (
        <BlendPriceImportModal
          book={book}
          onClose={() => setImporting(false)}
          onDone={(msg) => { setImporting(false); setMessage(msg); setError(null); load(); }}
        />
      )}

      {historyFor && (
        <BlendMaterialHistoryModal
          material={historyFor}
          columns={columns}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {editMaterial && (
        <BlendMaterialModal
          material={editMaterial.id ? editMaterial : null}
          onClose={() => setEditMaterial(null)}
          onSaved={(msg) => { setEditMaterial(null); setMessage(msg); load(); }}
        />
      )}
    </div>
  );
}
